/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name whitespace */

import { DockManager    } from 'dock-spawn-ts/lib/js/DockManager';
import { PanelContainer } from 'dock-spawn-ts/lib/js/PanelContainer';
import { PanelType      } from 'dock-spawn-ts/lib/js/enums/PanelType';

import { Logger         } from '@/util/logger';

import * as $ from 'jquery';
import KeyDownEvent = JQuery.KeyDownEvent;
import KeyUpEvent = JQuery.KeyUpEvent;

/* the kinds of windows */
export enum WindowType {
  Project,
  Properties,
  Search,
  FileBrowser,
  JSONBrowser,
  TextEditor,
  StringLineViewer,
  ScenarioBrowser,
  WorkBloxEditor,
  DiversionEditor,
  TSOEditor,
  Jobs,
  KPITable,
  Start,
  UserLogViewer,
  BatchViewer,
  RegressionViewer,
  ConnectionViewer,
  None
}

/**
 *
 * @class Window
 *
 * @brief base class for hosting/wrapping a PanelContainer from the Dock Spawn dockable viewport
 * framework.  All of our "views" like Projects, Properties, Search etc.  Are dockable viewports
 * (instances of PanelContainer), but we want to provide some abstraction around that so that
 * basic operations like opening a view don't require the rest of our application to know about
 * dock spawn...or whatever we happen to be using for dockable view support.
 *
 * The dock spawn project
 * @link https://github.com/node-projects/dock-spawn-ts
 *
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class Window {

  /** the CSS selector (#id) for where to host the window in the DOM */
  private               _id: string = '';

  /** what kind of window */
  private readonly    _type: WindowType = WindowType.None;

  /** the document manger we are spawned within */
  private readonly _manager: DockManager = null;

  /** the current Dock Spawn panel...goes away when window is closed */
  private            _panel: PanelContainer = null;

  /** the window title */
  private            _title: string = 'A Window';

  /** the window icon */
  private             _icon: string = 'Icon';

  /** the CSS selector we use id the hosting DOM element */
  private       _hostSelect: string = '';

  /** the DOM element that hosts the panel */
  private          _hostDOM: string = '';

  /** flag for windows that are tabs in a managed tab container */
  private tabbed: boolean = false;

  /** general error status, set to false if the window is in an error state */
  private ready: boolean = false;

  /** convert window type to string */
  public static stringOfType(type: WindowType) {

    switch (type) {
      case WindowType.Project:          return 'project';
      case WindowType.Properties:       return 'properties';
      case WindowType.Search:           return 'search';
      case WindowType.FileBrowser:      return 'filebrowser';
      case WindowType.StringLineViewer: return 'stringlineviewer';
      case WindowType.ScenarioBrowser:  return 'scenariobrowser';
      case WindowType.WorkBloxEditor:   return 'workbloxeditor';
      case WindowType.Jobs:             return 'jobs';
      case WindowType.Start:            return 'start';
      case WindowType.None:             return 'none';
    }

    return 'none';
  }

  /** convert blob type string to blob type */
  public static typeofString(text: string) : WindowType {

    text = text.toLowerCase().trim();

    switch (text) {
      case 'project':          return WindowType.Project;
      case 'properties':       return WindowType.Properties;
      case 'search':           return WindowType.Search;
      case 'filebrowser':      return WindowType.FileBrowser;
      case 'stringlineviewer': return WindowType.StringLineViewer;
      case 'scenariobrowser':  return WindowType.ScenarioBrowser;
      case 'workbloxeditor':   return WindowType.WorkBloxEditor;
      case 'jobs':             return WindowType.Jobs;
      case 'start':            return WindowType.Start;
      case 'none':             return WindowType.None;
    }

    return WindowType.None;
  }

  /** standard constructor */
  constructor(
    id:      string,
    manager: DockManager,
    type:    WindowType,
    title:   string = 'A Window',
    icon:    string = '',
    tabbed: boolean = false
  ) {

    this._id      = id;
    this._manager = manager;
    this._type    = type;
    this.tabbed   = tabbed;
    this.ready    = true;

    /* try to create the panel */

    this.panel();

    this.title   = title;

    if (this.icon !== '') {

      this.icon = icon;
    }



    /* force the styling on the panel */



  }

  /** force the window into an error state */
  public unReady(): void {

    this.ready = false;
  }

  /** force the window to be ready */
  public makeReady(): void {

    this.ready = true;
  }

  /** returns true if the window is not in any error state and is ready for use! */
  public isReady(): boolean {

    return this.ready;
  }

  /** fetch the id of this window */
  public id() {

    return this._id;
  }

  /** sub-classes (implementing windows), must do something appropriate for the save action */
  public save() {

    Logger.debug('[Window] type=' + Window.stringOfType(this._type) + ' Saving content...');
  }

  /* set the id and HTML to use for the hosting DOM element */
  public host(id: string, innerHTML: string) {

    this._hostSelect = id;
    this._hostDOM    = innerHTML;
  }

  /** make sure the hosting dom element exists */
  public ensureHost() {

    if ($(this._hostSelect).is('div') === true) {

      /* it's there already */

      return ;
    }

    /* have to (re)insert ... */

    const container: JQuery<HTMLElement> = $('#dock_div');

    if (!$(container).is('div')) {

      Logger.error('[Window] can not ensure host element, no dock_div container.');
      return ;
    }

    $(container).append(this._hostDOM);

  }

  public redraw(): void {

    /*
     * we can't do all the initial setup in construction; the DOM isn't in
     * the tree yet.
     *
     */

    Logger.debug('[Window] refreshing view: ', this._hostSelect);

    this._updateTitle();
  }

  private _updateTitle() {

    let text: string = this._title;

    if (text.length > 12) {

      text = text.substring(0, 12) + '...';
    }

    if (!this._panel || !this._icon) {

      /* nothing to do, no panel, or no icon. (window is closed?) */

      return ;
    }

    Logger.debug('[Window] _updateTitle: ', this._title);

    this._panel.elementTitleText.innerHTML =
      '<span class="window-title-icon">' +
      this._icon +
      '</span><span class="window-title-title">' +
      text +
      '</span>';

    if (this._panel.tabPage) {

      if (this._panel.tabPage.handle.elementText) {

        this._panel.tabPage.handle.elementText.innerHTML =
          '<span  title="' + this._title + '" class="window-title-icon">' +
          this._icon +
          '</span><span title="' + this._title + '" class="window-title-title">' +
          text +
          '</span>';
      }
    }

    this._panel.elementTitleText.innerHTML = text;

  }

  public get type() : WindowType {

    return this._type;
  }

  public get title(): string {

    /*
     * note: if you override the setter, you have to override the getter as well;
     * its ES6 JavaScript nonsense - they define both as a single attribute in
     * the class, so you if you override one, you have to override both.  You can
     * also just use regular properties instead.
     *
     */

    return this._title;
  }

  /** set the window title */
  public set title(title: string) {

    this._title = title;

    this._updateTitle();
  }

  /** test if the window is open or not */
  public isOpen() : boolean {

    if (this.panel(false) === null) {

      return false;
    }

    return true;
  }
  /** set the window icon */
  public set icon(icon: string) {

    this._icon = icon;

    this._updateTitle();
  }

  public get icon() {

    return this._icon;
  }

  /** fetch the dock span panel for this window */
  panel(auto: boolean = true): PanelContainer {

    if (!auto) {

      return this._panel;
    }

    /* we have one */

    if (this._panel !== null) {

      return this._panel;
    }

    /* is the view ready? */

    if (this._manager === null) {

      Logger.error('[Window] Can not make panel, no doc manager.');

      this.unReady();

      return null;
    }

    let id: string = this._id;

    if (!$(id).is('div')) {

      id = '#' + id;

      if (!$(id).is('div')) {

        Logger.error(`[Window] Can not make panel (${this._type}), no hosting div '${id}'.`);

        this.unReady();

        return null;
      }
    }

    /* create the panel */

    const pType: PanelType = this.tabbed ? PanelType.document : PanelType.panel;

    this._panel = new PanelContainer($(id).get(0), this._manager, this.title, pType);

    /* pass it back */

    return this._panel;
  }

  /** close the project view (if its open) */
  public close(implied: boolean = false): void {

    /*
     * @note this only gets called by our manual trigger of close, its not part of close
     * notification...which starts fro clicking on the close button in the corner.
     *
     */

    if(this._panel === null) {

      return ;
    }

    Logger.debug('[Window] closing ', this._title);

    /*
     * the dock spawn manager started the close action, so we can't "close", that would be
     * an infinite loop.  Just clear.
     *
     */

    if (!implied) {

      /* no, we did the close ourselves */

      this._panel.close();
    }

    this._panel = null;

  }

  /** open the project view (if its closed) */
  public open() : PanelContainer {

    if (this._panel !== null) {

      return this._panel;
    }

    Logger.debug('[Window] opening ', this._title);

    this.ensureHost();

    /*
     * block the Escape and Backspace keys on the content of the pane; so that default action of
     * DockSpawn (to close the window) is not taken.  We don't want users clicking around and
     * pressing Escape by accident...to kill a window that might have open edits or other important
     * info.
     *
     */

    $(`${this._hostSelect}`).on('keydown', (e: KeyDownEvent) => {

      if ((e.key === 'Enter') || (e.key === 'Tab') || (e.key === 'Escape') || (e.key === 'Backspace')) {

        e.preventDefault();
        e.stopPropagation();

        return false;
      }

    });

    $(`${this._hostSelect}`).on('keyup', (e: KeyUpEvent) => {

      if ((e.key === 'Enter') || (e.key === 'Tab') || (e.key === 'Escape') || (e.key === 'Backspace')) {

        e.preventDefault();
        e.stopPropagation();

        return false;
      }

    });


    return this.panel();
  }

}

